import React, { useState, useEffect } from 'react';
import './App.css';
import { Home, Activity, AlertTriangle, Clock, Bell, Users, Navigation, Settings, User, Zap, TrendingUp, Droplet, Repeat, Package, Shield, Trash2, Menu, ChevronLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import HomeOverview from './components/HomeOverview';
import HomeMonitoring from './components/HomeMonitoring';
import ActivityTracking from './components/ActivityTracking';
import HealthMetrics from './components/HealthMetrics';
import FallDetection from './components/fall-detection/FallDetection';
import SleepAnalysis from './components/SleepAnalysis';
import GaitAnalysis from './components/GaitAnalysis';
import CognitiveHealth from './components/cognitive-health/CognitiveHealth';
import EmergencyAlerts from './components/EmergencyAlerts';
import NightTimeActivity from './components/NightTimeActivity';
import DailyRoutine from './components/DailyRoutine';
import SocialInteractions from './components/SocialInteractions';
import WellnessScore from './components/WellnessScore';
import SafetyAlerts from './components/SafetyAlerts';
import LocationTracking from './components/LocationTracking';
import PhysicianReport from './components/PhysicianReport';
import DeviceSetup from './components/DeviceSetup';
import UserSettings from './components/UserSettings';
import MobilityHealth from './components/MobilityHealth';
import SmartHomeIntegration from './components/SmartHomeIntegration';
import CaregiverPortal from './components/CaregiverPortal';
import AdvancedGaitAnalysis from './components/AdvancedGaitAnalysis';
import BathroomSafety from './components/BathroomSafety';
import RecoveryMonitoring from './components/RecoveryMonitoring';
import FootHealthMonitoring from './components/FootHealthMonitoring';
import MorningRoutine from './components/morning-routine/MorningRoutine';
import SubtleChangeDetection from './components/subtle-change/SubtleChangeDetection';
import MyProducts from './components/MyProducts';
import IntelligentSecurityMonitor from './components/security-monitor/IntelligentSecurityMonitor';
import AdaptiveCleaningAutomation from './components/cleaning-automation/AdaptiveCleaningAutomation';
import PresenceAwareEnergyManagement from './components/energy-management/PresenceAwareEnergyManagement';

// Add interface for navigation items
interface NavItem {
  name: string;
  view: string;
  icon: any; // Use 'any' for now to fix the TypeScript issues with Lucide icons
  hidden?: boolean;
  path?: string; // URL path for this view
}

// Add interface for navigation sections
interface NavSection {
  title: string;
  items: NavItem[];
  color: string; // CSS color class for the section
}

interface EmergencyAlert {
  id: string;
  timestamp: Date;
  type: 'FALL' | 'MEDICAL' | 'BEHAVIORAL';
  status: 'ACTIVE' | 'RESPONDING' | 'RESOLVED';
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  location: string;
  description: string;
  vitalSigns?: {
    heartRate?: number;
    bloodPressure?: string;
    temperature?: number;
  };
  responseTeam?: {
    name: string;
    eta?: number;
    status: 'DISPATCHED' | 'EN_ROUTE' | 'ON_SCENE';
  };
}

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [currentView, setCurrentView] = useState('overview');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [alerts, setAlerts] = useState<EmergencyAlert[]>([
    {
      id: 'ALT-001',
      type: 'FALL',
      status: 'ACTIVE',
      priority: 'HIGH',
      timestamp: new Date(Date.now() - 3600000), // 1 hour ago
      location: 'Bathroom',
      description: 'Increased unsteady gait detected',
      vitalSigns: {
        heartRate: 92,
        bloodPressure: '145/95',
        temperature: 98.6
      },
      responseTeam: {
        name: 'Emergency Response Unit 1',
        eta: 8,
        status: 'EN_ROUTE'
      }
    },
    {
      id: 'ALT-002',
      type: 'BEHAVIORAL',
      status: 'RESPONDING',
      priority: 'MEDIUM',
      timestamp: new Date(Date.now() - 86400000), // 1 day ago
      location: 'Bedroom',
      description: 'Changes in sleep pattern detected',
      vitalSigns: {
        heartRate: 75,
        bloodPressure: '128/82'
      }
    },
    {
      id: 'ALT-003',
      type: 'MEDICAL',
      status: 'ACTIVE',
      priority: 'LOW',
      timestamp: new Date(Date.now() - 172800000), // 2 days ago
      location: 'Living Room',
      description: 'Decreased daily activity level',
      vitalSigns: {
        heartRate: 68,
        bloodPressure: '118/75',
        temperature: 98.2
      }
    }
  ]);

  // Define navigation sections with paths for router
  const navSections: NavSection[] = [
    {
      title: "Home Monitoring",
      color: "bg-blue-100 text-blue-800",
      items: [
        { name: 'Home Overview', view: 'overview', icon: Home, path: '/' },
        { name: 'Emergency Alerts', view: 'emergency', icon: Bell, path: '/emergency' },
        { name: 'Safety Alerts', view: 'safety', icon: Navigation, path: '/safety' },
        { name: 'Fall Detection', view: 'falls', icon: AlertTriangle, path: '/falls' }
      ]
    },
    {
      title: "Advanced Analysis",
      color: "bg-purple-100 text-purple-800",
      items: [
        { name: 'Advanced Gait Analysis', view: 'advanced-gait', icon: TrendingUp, path: '/advanced-gait' },
        { name: 'Foot Health', view: 'foot-health', icon: Activity, path: '/foot-health' },
        { name: 'Subtle Change', view: 'subtle-change', icon: TrendingUp, path: '/subtle-change' },
        { name: 'Bathroom Safety', view: 'bathroom', icon: Droplet, path: '/bathroom' }
      ]
    },
    {
      title: "Smart Home",
      color: "bg-green-100 text-green-800",
      items: [
        { name: 'Morning Routine', view: 'morning-routine', icon: Clock, path: '/morning-routine' },
        { name: 'Intelligent Security', view: 'security-monitor', icon: Shield, path: '/security-monitor' },
        { name: 'Cleaning Automation', view: 'cleaning-automation', icon: Trash2, path: '/cleaning-automation' },
        { name: 'Energy Management', view: 'energy-management', icon: Zap, path: '/energy-management' }
      ]
    },
    {
      title: "Caregiver Portal",
      color: "bg-amber-100 text-amber-800",
      items: [
        { name: 'Caregiver Portal', view: 'caregiver', icon: Users, path: '/caregiver' }
      ]
    },
    {
      title: "Setup and Settings",
      color: "bg-gray-100 text-gray-800",
      items: [
        { name: 'My Products', view: 'products', icon: Package, path: '/products' },
        { name: 'Device Setup', view: 'device', icon: Settings, path: '/device' },
        { name: 'User Settings', view: 'settings', icon: Settings, path: '/settings' }
      ]
    }
  ];

  // Create a flat array of all navigation items for rendering components
  const allNavItems: NavItem[] = navSections.flatMap(section => section.items);

  // Hidden items (still accessible via direct links but not in the sidebar)
  const hiddenNavItems: NavItem[] = [
    { name: 'Recovery Monitoring', view: 'recovery', icon: Repeat, hidden: true, path: '/recovery' },
    { name: 'Cognitive Health', view: 'cognitive', icon: Zap, hidden: true, path: '/cognitive' },
    { name: 'Social Activity', view: 'social', icon: Users, hidden: true, path: '/social' },
    { name: 'Smart Home', view: 'smarthome', icon: Home, hidden: true, path: '/smarthome' }
  ];

  // Combine all nav items for component rendering
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const combinedNavItems = [...allNavItems, ...hiddenNavItems];

  const bottomNavItems: NavItem[] = [
    { name: 'Overview', view: 'overview', icon: Home, path: '/' },
    { name: 'Morning', view: 'morning-routine', icon: Clock, path: '/morning-routine' },
    { name: 'Security', view: 'security-monitor', icon: Shield, path: '/security-monitor' },
    { name: 'Cleaning', view: 'cleaning-automation', icon: Trash2, path: '/cleaning-automation' },
    { name: 'Energy', view: 'energy-management', icon: Zap, path: '/energy-management' },
    { name: 'Foot', view: 'foot-health', icon: Activity, path: '/foot-health' },
    { name: 'Alerts', view: 'emergency', icon: Bell, path: '/emergency' }
  ];

  // Sync URL with current view
  useEffect(() => {
    // Find the correct view based on current path
    const path = location.pathname;
    const navItem = [...allNavItems, ...hiddenNavItems].find(item => item.path === path);
    
    if (navItem) {
      setCurrentView(navItem.view);
    } else if (path === '/') {
      setCurrentView('overview');
    }
  }, [location.pathname, allNavItems, hiddenNavItems]);

  // Handler to update view and URL
  const handleViewChange = (view: string) => {
    setCurrentView(view);
    
    // Find the corresponding path to navigate to
    const navItem = [...allNavItems, ...hiddenNavItems].find(item => item.view === view);
    if (navItem && navItem.path) {
      navigate(navItem.path);
    }
  };

  const renderComponent = (selectedComponent: string) => {
    switch (selectedComponent) {
      case 'overview':
        return <HomeOverview onNavigate={handleViewChange} />;
      case 'home':
        return <HomeMonitoring />;
      case 'activity':
        return <ActivityTracking />;
      case 'health':
        return <HealthMetrics />;
      case 'mobility':
        return <MobilityHealth />;
      case 'falls':
        return <FallDetection />;
      case 'sleep':
        return <SleepAnalysis />;
      case 'gait':
        return <GaitAnalysis />;
      case 'foot-health':
        return <FootHealthMonitoring />;
      case 'cognitive':
        return <CognitiveHealth />;
      case 'smarthome':
        return <SmartHomeIntegration />;
      case 'emergency':
        return <EmergencyAlerts alerts={alerts} />;
      case 'night':
        return <NightTimeActivity />;
      case 'routine':
        return <DailyRoutine />;
      case 'morning-routine':
        return <MorningRoutine />;
      case 'subtle-change':
        return <SubtleChangeDetection />;
      case 'social':
        return <SocialInteractions />;
      case 'wellness':
        return <WellnessScore />;
      case 'safety':
        return <SafetyAlerts />;
      case 'bathroom':
        return <BathroomSafety />;
      case 'location':
        return <LocationTracking />;
      case 'physician':
        return <PhysicianReport />;
      case 'caregiver':
        return <CaregiverPortal />;
      case 'device':
        return <DeviceSetup />;
      case 'settings':
        return <UserSettings />;
      case 'advanced-gait':
        return <AdvancedGaitAnalysis />;
      case 'recovery':
        return <RecoveryMonitoring />;
      case 'products':
        return <MyProducts />;
      case 'security-monitor':
        return <IntelligentSecurityMonitor />;
      case 'cleaning-automation':
        return <AdaptiveCleaningAutomation />;
      case 'energy-management':
        return <PresenceAwareEnergyManagement />;
      default:
        return <HomeOverview />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Persistent Sidebar */}
      <aside 
        className={`fixed top-0 left-0 bottom-0 bg-white shadow-lg z-50 transition-all duration-300 ${
          isSidebarCollapsed ? 'w-16' : 'w-64'
        } overflow-y-auto`}
      >
        <div className="p-4 border-b flex justify-between items-center">
          {!isSidebarCollapsed && <h2 className="text-lg font-semibold">Menu</h2>}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            {isSidebarCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>
        <nav className="py-2">
          {navSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="mb-4">
              {!isSidebarCollapsed && (
                <div className={`px-4 py-2 ${section.color} mx-2 rounded-md mb-2`}>
                  <h3 className="font-medium text-sm">{section.title}</h3>
                </div>
              )}
              <div className="space-y-1">
                {section.items.map((item) => (
                  <button
                    key={item.view}
                    onClick={() => handleViewChange(item.view)}
                    title={isSidebarCollapsed ? item.name : ''}
                    className={`w-full py-2 flex items-center ${
                      isSidebarCollapsed ? 'justify-center' : 'px-6'
                    } ${
                      currentView === item.view 
                        ? 'text-blue-500 bg-blue-50' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <item.icon size={18} />
                    {!isSidebarCollapsed && <span className="ml-3">{item.name}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Right Side Content */}
      <div className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
        {/* Header */}
        <header className="fixed top-0 right-0 bg-white shadow-sm z-40 h-14 transition-all duration-300" style={{ left: isSidebarCollapsed ? '4rem' : '16rem' }}>
          <div className="flex items-center justify-between h-full px-4">
            <div className="flex items-center space-x-4">
              <img src="/scanalytics-logo.png" alt="Scanalytics" className="h-8" />
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <h2 className="text-sm font-medium text-gray-900">Emma Martinez</h2>
                <p className="text-xs text-gray-500">Last updated: Just now</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Bell 
                    size={24} 
                    className="text-gray-600 cursor-pointer hover:text-blue-500 transition-colors"
                  />
                  {alerts.some(alert => alert.status === 'ACTIVE') && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                  )}
                </div>
                <button
                  onClick={() => handleViewChange('settings')}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <User size={24} className="text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-16 pb-20 px-4">
          {renderComponent(currentView)}
        </main>

        {/* Bottom Navigation */}
        <nav className="fixed bottom-0 right-0 bg-white border-t border-gray-200 z-40 transition-all duration-300" style={{ left: isSidebarCollapsed ? '4rem' : '16rem' }}>
          <div className="flex justify-around items-center h-16">
            {bottomNavItems.map((item) => (
              <button
                key={item.view}
                onClick={() => handleViewChange(item.view)}
                className={`flex flex-col items-center justify-center w-full h-full space-y-1
                  ${currentView === item.view 
                    ? 'text-blue-500' 
                    : 'text-gray-600 hover:text-blue-500'
                  }`}
              >
                <item.icon size={24} />
                <span className="text-xs">{item.name}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>
    </div>
  );
};

export default App;